/**
 * ⭐ Supplements' ONE way of writing a date (the polish round, owner 2026-10-24 — POL-3). Before
 * this, one date read up to six ways and two different functions were both called `dayMonth`
 * ("Nov 16" in Stats, "Mon 16 Nov" in the editor for the same run-out day).
 *
 *  · `dayLabel` — a day in running text: "Sun 25" near today, "Nov 16" further off.
 *  · `shortDay` — a day inside a week on screen (the strip, the box, a range): "Sun 25".
 *  · `longDay` — a day in full, for screen readers: "Tuesday 20 October".
 *  · `weekRangeLabel` — a week, Monday to Sunday: "Oct 19 – 25", "Oct 26 – Nov 1".
 *  · `keepClocks` — a line's clock times kept whole: "7:30 AM" never breaks before its AM.
 *
 * All fixed English, like every other word on these surfaces — never the browser's locale (one
 * style can't split by machine). Weekdays are the engine's (`weekdayOf`, `WEEKDAY_SHORT` in
 * utils/intakeSchedule.ts); clock times are too (`clockText` / `timeLabel`), in the app's
 * 12/24-hour setting.
 */
import { daysBetween, weekdayOf, WEEKDAY_SHORT } from '../../../utils/intakeSchedule';
import { shiftYMD } from '../../../utils/hydration';

export { weekdayOf, WEEKDAY_SHORT };

const WD_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = MONTH.map(m => m.slice(0, 3));

/** "Sun 25" — the weekday and the day (a day inside a week on screen). */
export function shortDay(ymd: string): string {
  return `${WEEKDAY_SHORT[weekdayOf(ymd)]} ${Number(ymd.slice(8))}`;
}

/**
 * A day in running text. Within six days of today, either way, the weekday and the day:
 * "Sun 25", "since Mon 19" (a weekday names one day in that span). Further off, the month and
 * day: "Nov 16", "since Oct 6" — with the year only when it isn't this year's ("Nov 1, 2025").
 */
export function dayLabel(ymd: string, today: string): string {
  if (Math.abs(daysBetween(today, ymd)) <= 6) return shortDay(ymd);
  const [y, m, d] = ymd.split('-').map(Number);
  const md = `${MONTH_SHORT[m - 1]} ${d}`;
  return ymd.slice(0, 4) === today.slice(0, 4) ? md : `${md}, ${y}`;
}

/** "Tuesday 20 October" — a day in full, as the screen-reader lists say it. */
export function longDay(ymd: string): string {
  const [, m, d] = ymd.split('-').map(Number);
  return `${WD_LONG[weekdayOf(ymd)]} ${d} ${MONTH[m - 1]}`;
}

/**
 * The week from `monday`, in fixed English like every date here: "Oct 19 – 25", or "Oct 26 – Nov 1"
 * across a month (the Week tab's header — never the browser's locale).
 */
export function weekRangeLabel(monday: string): string {
  const sunday = shiftYMD(monday, 6);
  const md = (ymd: string) => `${MONTH_SHORT[Number(ymd.slice(5, 7)) - 1]} ${Number(ymd.slice(8))}`;
  return `${md(monday)} – ${monday.slice(0, 7) === sunday.slice(0, 7) ? Number(sunday.slice(8)) : md(sunday)}`;
}

/**
 * Each 12-hour clock in `text` kept whole: a no-break space between the time and its AM/PM, so
 * "7:30 AM" never wraps before "AM" ("7:00–7:30 AM" too). A 24-hour clock has nothing to keep.
 */
export const keepClocks = (text: string): string => text.replace(/(\d{1,2}:\d{2}) (AM|PM)\b/g, '$1\u00A0$2');
